import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getMyReviewItems, getReviewTags, submitReview } from '../../api/real/reviewsApi';

/** The signed-in customer's ratings of their delivered items, keyed by order item id. */
export const useMyReviews = (enabled) => {
  const [byItem, setByItem] = useState({});
  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const items = await getMyReviewItems();
      const map = {};
      items.forEach((i) => { map[i.orderItemId] = i; });
      setByItem(map);
    } catch { /* rating stays unavailable */ }
  }, [enabled]);
  useEffect(() => { refresh(); }, [refresh]);
  return { byItem, refresh };
};

// The store's rating tags, loaded once per visit and shared by every RateItem on the page.
let tagsRequest = null;
const loadTags = () => {
  if (!tagsRequest) {
    tagsRequest = Promise.resolve()
      .then(() => getReviewTags())
      .then((t) => ({ positive: t?.positive || [], negative: t?.negative || [] }))
      .catch(() => { tagsRequest = null; return null; });
  }
  return tagsRequest;
};

/** { positive, negative } — the tags customers can pick (4–5★ / 1–3★); null until loaded or when unavailable. */
export const useReviewTags = () => {
  const [tags, setTags] = useState(null);
  useEffect(() => {
    let alive = true;
    loadTags().then((t) => { if (alive) setTags(t); });
    return () => { alive = false; };
  }, []);
  return tags;
};

export const MAX_TAGS = 3;
const STATUS_TEXT = { PENDING: 'Thanks — it will appear after a quick check', APPROVED: 'Published', REJECTED: 'Not published' };

/**
 * Under a delivered item in My Orders: rate it once — stars, up to 3 tags that match the stars, optional words.
 * {@code submit} defaults to the signed-in customer's rating call; the review e-mail page passes its own (the e-mail's
 * link instead of a sign-in) and may open the form with the star the customer tapped in the e-mail.
 */
const RateItem = ({ itemId, entry, onDone, submit = submitReview, defaultOpen = false, initialRating = 0 }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rating, setRating] = useState(initialRating >= 1 && initialRating <= 5 ? initialRating : 0);
  const [picked, setPicked] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const tags = useReviewTags();

  if (!entry) return null;                       // not a delivered item of this customer
  if (entry.review) {
    return (
      <div className="mt-1.5" data-testid="my-rating">
        <p className="text-xs text-ink-soft">
          You rated <span className="text-amber-400">{'★'.repeat(entry.review.rating)}</span>
          <span className="text-gray-200">{'★'.repeat(5 - entry.review.rating)}</span> · {STATUS_TEXT[entry.review.status] || entry.review.status}
        </p>
        {entry.review.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {entry.review.tags.map((t) => <span key={t} className="text-2xs bg-primary-50 text-primary px-2 py-0.5 rounded-full">{t}</span>)}
          </div>
        )}
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="mt-1.5 text-xs font-semibold text-primary hover:underline">
        Rate this item
      </button>
    );
  }

  const liked = rating >= 4;
  const options = !rating || !tags ? [] : (liked ? tags.positive : tags.negative);
  const choose = (n) => {
    if ((n >= 4) !== (rating >= 4)) setPicked([]);        // a 5★ "liked" tag can't stay on a 2★ rating
    setRating(n);
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < MAX_TAGS ? [...p, id] : p));

  const send = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error('Choose 1 to 5 stars'); return; }
    setSaving(true);
    try {
      await submit({ orderItemId: itemId, rating, title, body, ...(picked.length ? { tagIds: picked } : {}) });
      toast.success('Thank you for your rating!');
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not send your rating');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={send} className="mt-2 space-y-2.5 bg-surface rounded-xl border border-border p-3" data-testid="rate-form">
      <div className="flex gap-1" role="radiogroup" aria-label="Your rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? 's' : ''}`}
            onClick={() => choose(n)} className={`text-2xl leading-none ${n <= rating ? 'text-amber-400' : 'text-gray-200'}`}>★</button>
        ))}
      </div>
      {options.length > 0 && (
        <div data-testid="rate-tags">
          <p className="text-xs font-medium text-ink mb-1.5">
            {liked ? 'What did you like?' : 'What could be better?'} <span className="font-normal text-ink-muted">Pick up to {MAX_TAGS}</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {options.map((t) => {
              const on = picked.includes(t.id);
              const full = !on && picked.length >= MAX_TAGS;
              return (
                <button key={t.id} type="button" aria-pressed={on} disabled={full} onClick={() => toggle(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-ink border-border hover:border-primary/50'} ${full ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <input className="input-field w-full text-sm" placeholder="Title (optional)" maxLength={200}
        value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="input-field w-full text-sm min-h-[70px]" placeholder="Tell others what you think (optional)"
        maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-ink-soft px-3 py-2">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary btn-sm disabled:opacity-60">
          {saving ? 'Sending…' : 'Send rating'}
        </button>
      </div>
    </form>
  );
};

export default RateItem;
